import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import { useUserStore } from './store/userStore';
import { useTokenGuard } from './hooks/useTokenGuard';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import WorkflowBuilderPage from './pages/WorkflowBuilderPage';
import ConversationsPage from './pages/ConversationsPage';
import ConversationBoardPage from './pages/ConversationBoardPage';
import CompaniesPage from './pages/CompaniesPage';
import UsersManagementPage from './pages/UsersManagementPage';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import Button from './components/Button';

// Guard de roles: redirige a /conversations si el rol no está permitido.
// Mientras user es null (perfil aún cargando), no redirige para evitar
// redirects prematuros antes de conocer el rol real.
interface RequireRoleProps {
  allowedRoles: string[];
  children: React.ReactNode;
}
function RequireRole({ allowedRoles, children }: RequireRoleProps) {
  const { user } = useUserStore();
  if (user === null) return null; // esperando perfil
  if (!allowedRoles.includes(user.role)) {
    return <Navigate to="/conversations" replace />;
  }
  return <>{children}</>;
}

// Redirect de la ruta raíz según rol del usuario autenticado
function RootRedirect() {
  const { user, isLoading } = useUserStore();
  const { isAuthenticated } = useAuthStore();

  console.log('[RootRedirect] render — user:', user, '| isLoading:', isLoading, '| isAuthenticated:', isAuthenticated);

  // Perfil aún no disponible — esperar
  if (user === null) {
    if (!isAuthenticated) {
      console.log('[RootRedirect] → /login (no autenticado)');
      return <Navigate to="/login" replace />;
    }
    console.log('[RootRedirect] → esperando perfil...');
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-bg">
        <div className="text-center">
          <div className="w-10 h-10 rounded-2xl bg-brand-dark flex items-center justify-center text-brand-yellow font-bold text-base mx-auto mb-3">K</div>
          <p className="text-brand-mid text-sm">Cargando...</p>
        </div>
      </div>
    );
  }

  console.log('[RootRedirect] → role:', user.role, '→', user.role === 'administrador' ? '/dashboard' : '/conversations');
  if (user.role === 'administrador') return <Navigate to="/dashboard" replace />;
  return <Navigate to="/conversations" replace />;
}

// Componente interno que vive dentro del Router para poder usar useNavigate
function AppRoutes() {
  const { isAuthenticated } = useAuthStore();
  const { showModal, handleAccept } = useTokenGuard();

  return (
    <div className="min-h-screen bg-brand-bg">
      <Routes>
        <Route path="/login" element={<LoginPage />} />

        <Route
          path="/*"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Layout>
                <Routes>
                  <Route
                    path="/dashboard"
                    element={
                      <RequireRole allowedRoles={['administrador']}>
                        <DashboardPage />
                      </RequireRole>
                    }
                  />
                  <Route
                    path="/users"
                    element={
                      <RequireRole allowedRoles={['administrador']}>
                        <UsersManagementPage />
                      </RequireRole>
                    }
                  />
                  <Route
                    path="/companies"
                    element={
                      <RequireRole allowedRoles={['administrador', 'operador']}>
                        <CompaniesPage />
                      </RequireRole>
                    }
                  />
                  <Route
                    path="/workflows"
                    element={
                      <RequireRole allowedRoles={['administrador', 'operador']}>
                        <WorkflowBuilderPage />
                      </RequireRole>
                    }
                  />
                  <Route
                    path="/workflows/:id"
                    element={
                      <RequireRole allowedRoles={['administrador', 'operador']}>
                        <WorkflowBuilderPage />
                      </RequireRole>
                    }
                  />
                  <Route path="/conversations" element={<ConversationsPage />} />
                  <Route path="/board" element={<ConversationBoardPage />} />
                  <Route path="/" element={<RootRedirect />} />
                </Routes>
              </Layout>
            </ProtectedRoute>
          }
        />
      </Routes>

      {/* Modal sesión expirada */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4">
          <div className="bg-brand-surface rounded-2xl shadow-2xl max-w-sm w-full border border-brand-border overflow-hidden">
            {/* Ícono */}
            <div className="flex flex-col items-center px-8 pt-8 pb-6 text-center">
              <div className="w-16 h-16 rounded-2xl bg-brand-yellow/20 flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-brand-yellow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h2 className="text-lg font-bold text-brand-dark mb-2">Sesión expirada</h2>
              <p className="text-sm text-brand-mid leading-relaxed">
                Tu sesión ha caducado por seguridad. Por favor vuelve a iniciar sesión para continuar.
              </p>
            </div>
            {/* Acción */}
            <div className="px-8 pb-8">
              <Button variant="primary" fullWidth onClick={handleAccept}>
                Iniciar sesión
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function App() {
  const { isCheckingAuth, checkAuth } = useAuthStore();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Pantalla de carga mientras se verifica auth al arrancar
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-bg">
        <div className="text-center">
          <div className="w-12 h-12 rounded-2xl bg-brand-dark flex items-center justify-center text-brand-yellow font-bold text-lg mx-auto mb-4">
            K
          </div>
          <p className="text-brand-mid text-sm">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <AppRoutes />
    </Router>
  );
}

export default App;
